import React, { useEffect, useState, useMemo } from "react";
import { Button, DataTable } from "./ui";
import { api } from "../services";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBInput, MDBIcon, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem } from "mdb-react-ui-kit";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";

const PRINTER_TYPES = [
  { label: "Thermal", value: "thermal" },
  { label: "Laser", value: "laser" },
  { label: "DotMetrix", value: "dotmetrix" },
];
const PAPER_SIZES = [
  { label: "A4 (210 x 297 mm)", value: "A4", mm: "210x297" },
  { label: "A5 (148 x 210 mm)", value: "A5", mm: "148x210" },
  { label: "A6 (105 x 148 mm)", value: "A6", mm: "105x148" },
];
const TIMEZONES = [
  { label: "UTC+03:00 (KSA)", value: "+03:00" },
  { label: "UTC+04:00 (UAE)", value: "+04:00" },
  { label: "UTC+02:00 (Egypt)", value: "+02:00" },
  { label: "UTC+03:00 (Qatar/Bahrain/Kuwait)", value: "+03:00" },
  { label: "UTC+03:00 (Iraq)", value: "+03:00" },
  { label: "UTC+03:00 (Yemen)", value: "+03:00" },
  { label: "UTC+03:00 (Syria)", value: "+03:00" },
  { label: "UTC+03:00 (Jordan)", value: "+03:00" },
  { label: "UTC+03:00 (Palestine)", value: "+03:00" },
  { label: "UTC+03:00 (Lebanon)", value: "+03:00" },
  { label: "UTC+03:30 (Iran)", value: "+03:30" },
  { label: "UTC+04:30 (Afghanistan)", value: "+04:30" },
  { label: "UTC+01:00 (Morocco/Algeria/Tunisia)", value: "+01:00" },
  { label: "UTC+00:00 (GMT)", value: "+00:00" },
];
const BUSINESS_TIMEZONES = [
  { label: "KSA (Asia/Riyadh)", value: "Asia/Riyadh" },
  { label: "UAE (Asia/Dubai)", value: "Asia/Dubai" },
  { label: "Qatar (Asia/Qatar)", value: "Asia/Qatar" },
  { label: "Bahrain (Asia/Bahrain)", value: "Asia/Bahrain" },
  { label: "Kuwait (Asia/Kuwait)", value: "Asia/Kuwait" },
  { label: "Oman (Asia/Muscat)", value: "Asia/Muscat" },
  { label: "Egypt (Africa/Cairo)", value: "Africa/Cairo" },
  { label: "Jordan (Asia/Amman)", value: "Asia/Amman" },
  { label: "Lebanon (Asia/Beirut)", value: "Asia/Beirut" },
  { label: "Syria (Asia/Damascus)", value: "Asia/Damascus" },
  { label: "Iraq (Asia/Baghdad)", value: "Asia/Baghdad" },
  { label: "Palestine (Asia/Gaza)", value: "Asia/Gaza" },
  { label: "Iran (Asia/Tehran)", value: "Asia/Tehran" },
  { label: "Morocco (Africa/Casablanca)", value: "Africa/Casablanca" },
  { label: "Algeria (Africa/Algiers)", value: "Africa/Algiers" },
  { label: "Tunisia (Africa/Tunis)", value: "Africa/Tunis" },
];

function PrinterSettings() {
  const {t} = useTranslation("translation")
  const { can } = usePermission();
  const canAddPrinterConfig = can(RBAC_MODULES.PRINTER_SETTINGS, PERMISSION_ACTIONS.ADD);
  const canEditPrinterConfig = can(RBAC_MODULES.PRINTER_SETTINGS, PERMISSION_ACTIONS.EDIT);
  const canDeletePrinterConfig = can(RBAC_MODULES.PRINTER_SETTINGS, PERMISSION_ACTIONS.DELETE);
  const [settings, setSettings] = useState([]);
  const [tableSortColumn, setTableSortColumn] = useState("storeName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [stores, setStores] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [form, setForm] = useState({
    storeId: "",
    branchId: "",
    currency: "SAR",
    printerType: "thermal",
    paperSize: "A6",
    paperSizeMM: "105x148",
    billingType: "Billing",
    dateTimeLocal: "+03:00",
    logo: "",
    business_tax_number: "",
    business_timezone: "Asia/Riyadh",
    receipt_footer: "",
    receipt_header: "",
    receipt_hide_free_modifier_options: false,
    receipt_invoice_title: "",
    receipt_localized_language: "ar",
    receipt_main_language: "en",
    receipt_show_calories: false,
    receipt_show_check_number: true,
    receipt_show_closer_username: false,
    receipt_show_creator_username: true,
    receipt_show_order_number: true,
    receipt_show_rounding: true,
    receipt_show_subtotal: false,
    tax_registration_name: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Add loading state

  // Fetch stores for the user
  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(res.data.stores || []);
    } catch (err) {
      setStores([]);
    }
  };

  // Fetch branches for selected store
  const fetchBranches = async (storeId) => {
    if (!storeId) return setBranches([]);
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(res.data.branches || []);
    } catch (err) {
      setBranches([]);
    }
  };

  // Fetch all settings for the user (optionally filter by branch)
  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/printer-settings", {
        headers: { Authorization: `Bearer ${token}` },
        // params: form.branchId ? { branchId: form.branchId } : {},
      });
      setSettings(res.data.data || []);
    } catch (err) {
      setSettings([]);
    }
  };

  useEffect(() => {
    // Simulate data fetch
    const timer = setTimeout(() => setLoading(false), 1200);
    fetchStores();
    fetchSettings();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (form.storeId) {
      fetchBranches(form.storeId);
    } else {
      setBranches([]);
    }
  }, [form.storeId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDropdownChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "paperSize"
        ? { paperSizeMM: PAPER_SIZES.find((p) => p.value === value)?.mm || "" }
        : {}),
    }));
  };

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setForm((prev) => ({ ...prev, storeId: store._id, branchId: "" }));
    setSelectedBranch(null);
  };

  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch);
    setForm((prev) => ({ ...prev, branchId: branch._id }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      if (editingId) {
        await api.put(`/printer-settings/${editingId}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessage(t("Updated successfully"));
      } else {
        await api.post("/printer-settings", form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessage(t("Created successfully"));
      }
      setForm({ ...form, storeId: "", branchId: "" });
      setEditingId(null);
      fetchSettings();
    } catch (err) {
      setMessage(t("Error saving settings"));
    }
  };

  const handleEdit = (setting) => {
    navigate(`/printer-settings/${setting._id}/edit`);
  };

  const handleAdd = () => {
    navigate("/printer-settings/new");
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");
    try {
      await api.delete(`/printer-settings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(t("Deleted successfully"));
      fetchSettings();
    } catch (err) {
      setMessage(t("Error deleting"));
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "storeName",
        header: t("store"),
        sortable: true,
        render: (s) => s.storeName,
      },
      {
        id: "branchName",
        header: t("branch"),
        sortable: true,
        render: (s) => s.branchName,
      },
      {
        id: "printerType",
        header: t("type"),
        sortable: true,
        render: (s) => s.printerType,
      },
      {
        id: "paperSize",
        header: t("paper"),
        sortable: true,
        render: (s) => s.paperSize,
      },
      {
        id: "actions",
        header: t("actions"),
        hideable: false,
        render: (s) => (
          <div onClick={(e) => e.stopPropagation()}>
            {canEditPrinterConfig ? (
              <Button
                size="sm"
                color="info"
                outline
                className="me-1"
                onClick={() => handleEdit(s)}
              >
                <MDBIcon icon="edit" />
              </Button>
            ) : null}
            {canDeletePrinterConfig ? (
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => handleDelete(s._id)}
              >
                <MDBIcon icon="trash" />
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [t, canEditPrinterConfig, canDeletePrinterConfig]
  );

  const sortedSettings = useMemo(() => {
    const list = [...settings];
    const dir = sortDirection === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = String(a[tableSortColumn] || "").toLowerCase();
      const bv = String(b[tableSortColumn] || "").toLowerCase();
      return dir * av.localeCompare(bv);
    });
    return list;
  }, [settings, tableSortColumn, sortDirection]);

  const applyTableSort = (column, direction) => {
    setTableSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <div className="printer-settings-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("printerconfiguration")}</h1>
        <p className="oh-page-subtitle">{t("manageyourprinter")}</p>
        <div className="printer-table-responsive">
          <DataTable
            tableId="printer-settings"
            title={t("printerconfiguration")}
            columns={columns}
            data={sortedSettings}
            rowKey="_id"
            loading={loading}
            emptyState="No settings found"
            primaryAction={
              canAddPrinterConfig
                ? { label: t("addprinterconfig"), onClick: handleAdd }
                : {
                    label: t("addprinterconfig"),
                    onClick: handleAdd,
                    disabled: true,
                    title: t("noPermissionToAccessThis"),
                  }
            }
            sort={{
              column: tableSortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => applyTableSort(column, direction),
            }}
            onRowClick={canEditPrinterConfig ? handleEdit : undefined}
          />
        </div>
      </MDBContainer>
    </div>
  );
}

export default PrinterSettings;
